<template>
  <div class="settings-panel">
    <div class="viewer-info-box" v-if="showViewerCannot">
      <div class="flex gap-2 items-center p-2 bg-yellow-10 rounded">
        <span><img alt="giphy-star-icon" :src="require('@/assets/images/icon/warning-circle.svg')"></span>
        {{ $i18nPresenter.t(`You’re a viewer, so you can’t change any settings.`) }}
      </div>
    </div>

    <aha-accordion
      v-else
      class="settings-accordion"
      :class="{'overflow-y-auto': !canUseCollectParticipantInfo}"
    >
      <aha-accordion-item
        :default-expanded="defaultExpandedAccordion==='information'"
      >
        <template
          slot="title"
        >
          <span class="w-full h-full" ref="presentInformation" @click="onClickPresenInfoTab">
            <div class="flex items-center">
              {{ $i18nPresenter.t('Presentation info') }}
            </div>
          </span>
        </template>
        <template slot="content">
          <setting-metadata
            :presentation="presentation"
            :currentCategories="currentCategories"
          ></setting-metadata>
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion==='lang'">
        <template slot="title">
          <div>{{ $i18nPresenter.t('Presentation Language') }}</div>
        </template>

        <template slot="content">
          <div class="relative">
            <p class="mb-4 text-xs">
              {{ $i18nPresenter.t('Choose the language that your audience will see on their devices') }}
            </p>
            <div class="relative mb-3">
              <select
                class="w-full p-3 pr-8 border rounded outline-none appearance-none cursor-pointer
                  hover:border-primary-90 focus:border-primary-90 bg-base-0 focus:outline-none
                  border-secondary-20"
                v-model="$store.state.presentation.language"
                @change="setLang()"
              >
                <option v-for="language in languages" :key="language.id" :value="language.id">
                  {{ language.title }}
                </option>
              </select>
              <aha-icon
                class="absolute ml-auto pointer-events-none top-4 right-3"
                name="arrow"
                viewBox="0 0 24 24"
                width="12"
                height="12"
              />
            </div>

            <aha-checkbox
              class="mb-3 w-max"
              id="hideJoining"
              v-model="presentation.filteringProfanity"
              @change="handleUpdateProfanityFilter()"
            >
              {{ $i18nPresenter.t('Filter profanity') }}
              <i
                class="fas fa-question-circle mt-0.5 ml-1"
                v-tooltip="$i18nPresenter.t('Hide swear words by the audience (in their submissions and nicknames)')"
              ></i>
            </aha-checkbox>

            <aha-info-box class="mb-4">
              <p>
                {{ $i18nPresenter.t('Help us improve the translations or profanity filtering by emailing us at') }}
                <a
                  class="text-primary-90"
                  href="mailto:hi@ahaslides.com"
                >hi@ahaslides.com</a>.
              </p>
            </aha-info-box>

            <div class="overlay-disable-setting" v-if="!canEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion==='audienceAdmission'">
        <template slot="title">
          <div class="flex items-center">
            {{ $i18nPresenter.t('Collect audience info') }}
          </div>
        </template>
        <template slot="content">
          <SettingParticipantFields
            v-if="canUseCollectParticipantInfo"
            @open-when-user-not-paid="openUpgradeBackground"
          />
          <SettingAudienceAdmission
            v-else
            @open-when-user-not-paid="openUpgradeBackground"
          />
        </template>
      </aha-accordion-item>
      <aha-accordion-item :default-expanded="defaultExpandedAccordion==='audienceAuthentication'">
        <template slot="title">
          <div class="flex items-center">
            {{ $i18nPresenter.t('Audience authentication') }}
          </div>
        </template>
        <template slot="content">
          <SettingAudienceAuthentication />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion==='qna'">
        <template slot="title">
          <div>{{ $i18nPresenter.t('Q&A with your audience') }}</div>
        </template>
        <template slot="content">
          <SettingQna @updatePresentation="_updatePresentation" />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion=='quiz'" ref="generalQuizSettings">
        <template slot="title">
          <div class="flex items-center">
            {{ $i18nPresenter.t('General quiz settings') }}
          </div>
        </template>
        <template slot="content">
          <SettingQuiz
            @showTeamSetUp="isShowTeamSetup = !isShowTeamSetup"
            @hideMainHeader="handleHideMainHeader()"
            @updatePresentation="_updatePresentationFromQuizTeamConfig"
          />
        </template>
      </aha-accordion-item>

      <aha-accordion-item :default-expanded="defaultExpandedAccordion==='pacing'">
        <template slot="title">
          <div class="flex items-center">
            {{ $i18nPresenter.t('Who takes the lead') }}
          </div>
        </template>

        <template slot="content">
          <div class="relative">
            <AhaInfoBox class="mb-3">
              {{ $i18nPresenter.t(`What’s new: The audience (self-paced) option is now available for quiz questions.`) }}
            </AhaInfoBox>

            <aha-radio
              class="mb-3"
              name="whoTakesTheLead"
              :value="false"
              v-model="$store.state.presentation.audiencePacing"
              @change="onChangeAudiencePacing"
            >
              {{ $i18nPresenter.t('Presenter') }}
              <i
                class="fas fa-question-circle ml-1"
                v-tooltip="presenterPacingTooltip"
              ></i>
            </aha-radio>

            <aha-radio
              name="whoTakesTheLead"
              :value="true"
              v-model="$store.state.presentation.audiencePacing"
              @change="onChangeAudiencePacing"
            >
              {{ $i18nPresenter.t('Audience (self-paced)') }}
              <i
                class="fas fa-question-circle ml-1"
                v-tooltip="
                  $i18nPresenter.t('Participants start from the first slide ' +
                    'and proceed on their own. Great for surveys')
                "
              ></i>
            </aha-radio>
            <p class="mt-2 ml-5 text-xs" v-if="integrationPlatform !== 'GoogleSlide'">
              {{ $i18nPresenter.t('To invite your audience, please go to') }}
              <aha-link
                @click="showShareMenu"
                type="primary"
                variant="primary"
              >
                <span class="text-xs font-bold">{{ $i18nPresenter.t('Share') }}</span>
              </aha-link>.
            </p>

            <div class="overlay-disable-setting" v-if="!canEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>
      <aha-accordion-item
        :default-expanded="defaultExpandedAccordion==='other'"
      >
        <template slot="title">
          <div>{{ $i18nPresenter.t('Other settings') }}</div>
        </template>
        <template slot="content">
          <div class="relative">
            <div class="other-settings__content-wrapper">
              <aha-checkbox
                id="hideJoining"
                v-model="presentation.hideInstructionBar"
                @change="_hideInstructionBar()"
              >
                {{ $i18nPresenter.t('Hide the joining instruction bar') }}
              </aha-checkbox>
            </div>
            <div class="other-settings__content-wrapper">
              <aha-checkbox
                id="makeLinksClickable"
                v-model="presentation.showHyperLink"
                @change="_linkClickAble()"
              >
                {{ $i18nPresenter.t('Make links clickable') }}
              </aha-checkbox>
            </div>

            <div class="other-settings__content-wrapper">
              <aha-checkbox
                v-model="presentation.isEnablePrivateFeedback"
                class="group flex"
                @change="togglePrivateFeedback()"
              >
                <span>
                  {{ $i18nPresenter.t('Allow audience feedback') }}
                  <span
                    class="ml-1 text-secondary-40 cursor-pointer opacity-0 group-hover:opacity-100"
                    v-tooltip="$i18nPresenter.t('Audience can send you private feedback.')"
                  >
                    <i class="fas fa-question-circle" />
                  </span>
                </span>
              </aha-checkbox>
            </div>

            <div class="relative other-settings__content-wrapper">
              <div
                class="absolute top-0 left-0 w-full h-full z-10 cursor-pointer"
                v-if="planBenefit.privacyControl &&
                  !planBenefit.privacyControl.slidesReviewControl"
                @click="handleCheckNeedUpgradePlanSlideReview()"
              >
              </div>
              <aha-checkbox
                v-model="presentation.isEnableAudienceReviewSlides"
                class="group flex remove-css-disable"
                @change="toggleAudienceReview()"
                :disabled="planBenefit.privacyControl && !planBenefit.privacyControl.slidesReviewControl"
              >
                <span>
                  {{ $i18nPresenter.t('allow_audience_to_review_the_slides') }}
                  <span v-if="!canUseNewTooltip">
                    <IconNeedUpgradePlan
                      v-if="planBenefit.privacyControl"
                      ref="iconNeedUpgradePlanComponentSlidesReview"
                      :noPermission="planBenefit.privacyControl &&
                        !planBenefit.privacyControl.slidesReviewControl"
                      :defaultTooltip="$i18nPresenter.t('Audience can view all slides a' +
                        'nd answers at the end of the presentation.')"
                      left="-200px"
                      :upgradePlan="planBenefit.privacyControl.slidesReviewControl_planName"
                      featureName="Audience Review Slides"
                    />
                  </span>
                  <span v-else>
                    <UpgradeIcon
                      v-if="planBenefit.privacyControl"
                      ref="iconNeedUpgradePlanComponentSlidesReview"
                      :noPermission="planBenefit.privacyControl &&
                        !planBenefit.privacyControl.slidesReviewControl"
                      :defaultTooltip="$i18nPresenter.t('Audience can view all slides a' +
                        'nd answers at the end of the presentation.')"
                      :upgradePlan="planBenefit.privacyControl.slidesReviewControl_planName"
                      featureName="Audience Review Slides"
                    />
                  </span>
                </span>
              </aha-checkbox>
            </div>

            <div class="relative other-settings__content-wrapper mb-2">
              <div
                class="absolute top-0 left-0 w-full h-full z-10 cursor-pointer"
                v-if="planBenefit.brandingCustomisation &&
                  !planBenefit.brandingCustomisation.hideAhaSlidesLink"
                @click="handleCheckNeedUpgradeHideSlideLink()"
              ></div>
              <aha-checkbox
                v-model="presentation.isEnableAudienceAhaSlideLabel"
                class="group flex remove-css-disable"
                @change="toggleAudienceAhaSlideLabel()"
                :disabled="isDisableAhaSlideLink"
              >
                <span>
                  {{ $i18nPresenter.t('show_ahaslides_link_last_slide') }}

                  <span v-if="!canUseNewTooltip">
                    <IconNeedUpgradePlan
                      v-if="planBenefit.brandingCustomisation"
                      ref="iconNeedUpgradePlanComponentHideSlideLink"
                      :noPermission="planBenefit.brandingCustomisation &&
                        !planBenefit.brandingCustomisation.hideAhaSlidesLink"
                      :defaultTooltip="$i18nPresenter.t('Show the sign-up link for AhaSlides at the' +
                        ' end of your presentation.')"
                      left="-200px"
                      :upgradePlan="planBenefit.brandingCustomisation.hideAhaSlidesLink_planName"
                      featureName="Hide Slide Link"
                    />
                  </span>
                  <span v-else>
                    <UpgradeIcon
                      v-if="planBenefit.brandingCustomisation"
                      ref="iconNeedUpgradePlanComponentHideSlideLink"
                      :noPermission="planBenefit.brandingCustomisation &&
                        !planBenefit.brandingCustomisation.hideAhaSlidesLink"
                      :defaultTooltip="$i18nPresenter.t('Show the sign-up link for AhaSlides at the' +
                        ' end of your presentation.')"
                      :upgradePlan="planBenefit.brandingCustomisation.hideAhaSlidesLink_planName"
                      featureName="Hide Slide Link"
                    />
                  </span>
                </span>
              </aha-checkbox>
            </div>

            <SettingShowAccountTab
              v-if="!isWhiteLabelOrSkoletube"
              :isProPlan="isProPlan"
              :isBeforeCurrentPeriodStart="isBeforeCurrentPeriodStart"
              @updatePresentation="_updatePresentation"
            />

            <!-- Reaction setting -->
            <div class="mb-2 font-bold group flex">
              <span>
                {{ $i18nPresenter.t('Reactions') }}
                <span
                  class="ml-1 text-secondary-40 cursor-pointer opacity-0 group-hover:opacity-100"
                  v-tooltip="$i18nPresenter.t('Let the audience send reactions during your presentation')"
                >
                  <i class="fas fa-question-circle" />
                </span>
              </span>
            </div>
            <div class="flex mb-3">
              <aha-checkbox
                class="settings-reaction__checkbox"
                @change="handleEnableSingleReaction(reaction.name, $event)"
                v-model="presentation.reactions[reaction.name]"
                v-for="(reaction, index) in reactionImages"
                :key="index"
              >
                <img
                  :src="reaction.icon"
                  class="settings-reaction__image"
                  v-if="presentation.reactions[reaction.name]"
                >
                <img :src="reaction.iconDefault" class="settings-reaction__image" v-else />
              </aha-checkbox>
            </div>

            <template
              v-if="currentUser.role === 'admin' || currentUser.email === 'template@ahaslides.com'"
            >
              <div class="mb-3 font-bold">
                {{ $i18nPresenter.t('Tags') }}
              </div>
              <Tags class="mb-4" />
            </template>

            <div class="overlay-disable-setting" v-if="!canEditPresentation"></div>
          </div>
        </template>
      </aha-accordion-item>
    </aha-accordion>
  </div>
</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import Util from '@/util'
import Config from '@/config'
import Constant from '@/constant'
import Socket from '@/socket'
import EventBus from '@/eventBus'

// api
import PresentationApi from '@/api/presentation'
import eventCategories from '@/constant/eventCategories'

// components
import SettingAudienceAdmissionContainer from '@/containers/header-settings/SettingAudienceAdmissionContainer'
import SettingParticipantFields from '@/components-v2/presentation-editor/editor-header/SettingParticipantFields.vue'
import Tags from '@/components-v2/presentation-editor/editor-header/Tags.vue'
import SettingAudienceAdmission from '@/components-v2/presentation-editor/editor-header/SettingAudienceAdmission.vue'
import SettingAudienceAuthentication from '@/components-v2/presentation-editor/editor-header/SettingAudienceAuthentication.vue'
import SettingQna from '@/components-v2/presentation-editor/editor-header/SettingQnA.vue'
import SettingQuiz from '@/components-v2/presentation-editor/editor-header/SettingQuiz.vue'
import SettingMetadata from '@/components-v2/presentation-editor/editor-header/SettingMetadata.vue'
import IconNeedUpgradePlan from '@/components-v2/my-plan/IconNeedUpgradePlan.vue'
import UpgradeIcon from '@/components-v2/my-plan/UpgradeIcon.vue'
import SettingShowAccountTab from '@/components-v2/presentation-editor/editor-header/SettingShowAccountTab.vue'

// reactions
const like = require('@/assets/images/reactions/like.svg')
const likeDefault = require('@/assets/images/reactions/like-default.svg')
const heart = require('@/assets/images/reactions/heart.svg')
const heartDefault = require('@/assets/images/reactions/heart-default.svg')
const laugh = require('@/assets/images/reactions/laugh.svg')
const laughDefault = require('@/assets/images/reactions/laugh-default.svg')
const wow = require('@/assets/images/reactions/wow.svg')
const wowDefault = require('@/assets/images/reactions/wow-default.svg')
const sad = require('@/assets/images/reactions/sad.svg')
const sadDefault = require('@/assets/images/reactions/sad-default.svg')

const reactionImages = [
  {
    name: 'like',
    icon: like,
    iconDefault: likeDefault,
  },
  {
    name: 'heart',
    icon: heart,
    iconDefault: heartDefault,
  },
  {
    name: 'laugh',
    icon: laugh,
    iconDefault: laughDefault,
  },
  {
    name: 'wow',
    icon: wow,
    iconDefault: wowDefault,
  },
  {
    name: 'sad',
    icon: sad,
    iconDefault: sadDefault,
  },
]
const languages = [
  { id: 'id', title: 'Bahasa Indonesia' },
  { id: 'cs', title: 'Čeština' },
  { id: 'da', title: 'Dansk' },
  { id: 'de', title: 'Deutsch' },
  { id: 'en', title: 'English' },
  { id: 'es', title: 'Español' },
  { id: 'fr', title: 'Français' },
  { id: 'hr', title: 'Hrvatski' },
  { id: 'is', title: 'Íslenska' },
  { id: 'it', title: 'Italiano' },
  { id: 'lv', title: 'Latviešu Valoda' },
  { id: 'lt', title: 'Lietuvių' },
  { id: 'hu', title: 'Magyar' },
  { id: 'nl', title: 'Nederlands' },
  { id: 'no', title: 'Norsk' },
  { id: 'pl', title: 'Polski' },
  // code của portuguese phải là pt nên đã sửa lại
  { id: 'pt', title: 'Português' },
  { id: 'ro', title: 'Română' },
  { id: 'sq', title: 'Shqip' },
  { id: 'sk', title: 'Slovenčina' },
  { id: 'si', title: 'Slovenščina' },
  { id: 'se', title: 'Svenska' },
  { id: 'vi', title: 'Tiếng Việt' },
  { id: 'tr', title: 'Türkçe' },
  { id: 'bg', title: 'Български' },
  { id: 'el', title: 'Ελληνικά' },
  { id: 'ru', title: 'Pусский' },
  { id: 'ja', title: '日本語' },
  { id: 'zh', title: '中文' },
  { id: 'kr', title: '한국인' },
]

export default {
  components: {
    SettingAudienceAdmission: SettingAudienceAdmissionContainer(SettingAudienceAdmission),
    SettingParticipantFields,
    SettingQna,
    SettingQuiz,
    SettingMetadata,
    Tags,
    SettingAudienceAuthentication,
    IconNeedUpgradePlan,
    SettingShowAccountTab,
    UpgradeIcon,
  },
  data: () => ({
    languages,
    teamPlay: false,
    ctaUpgradeParticipantInfo: false,
    teamCount: Constant.defaultQuizTeam.teamCount,
    teamSize: Constant.defaultQuizTeam.teamSize,
    limit: 1200,
    reactionImages,
    reactions: {},
    ctaUpgrade: false,
    role: Constant.role,
    ctaUpgradeAudienceAhaLabel: false,
    defaultExpandedAccordion: null,
  }),
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      planBenefit: (state) => state.planBenefit,
      integrationPlatform: (state) => state.integrationPlatform,
    }),
    ...mapGetters('featureToggles', ['canUseNewTooltip']),
    ...mapGetters('presentationSetting', ['canUseCollectParticipantInfo']),
    currentCategories: {
      get() {
        return this.presentation.PresentationsCategories
      },
    },
    presenterPacingTooltip() {
      return this.$i18nPresenter.t('The audience follow you. They can\'t proceed to the next slide on their own.')
    },
    isPlanFree() {
      return !this.$store.state.plan || this.$store.state.plan.group === 'free'
    },
    isBeforeCurrentPeriodStart() {
      return this.$store.state.currentUser.isBeforeCurrentPeriodStart
    },
    isWhiteLabel() {
      return this.$store.state.plan.group === 'white-label'
    },
    isProPlan() {
      return this.$store.state.plan.group == 'pro'
    },
    isMSAccount() {
      return this.currentUser && this.currentUser.email && this.currentUser.email.includes('onmicrosoft.com')
    },
    isEURegion() {
      return Config.region === 'eu'
    },
    isNotShowAttendanceCheckBefore() {
      return !window.localStorage.getItem('showAttendancePopupBefore')
    },
    showViewerCannot() {
      if (this.presentation.permissions === undefined) {
        return false
      }
      return !this.presentation.permissions.includes(Constant.permissions.RENAME_PRESENTATION)
    },
    isWhiteLabelOrSkoletube() {
      return this.currentUser && (
        this.currentUser.plan === 'white-label' ||
        this.currentUser.identifyProvider === Constant.identityProviders.SKOLETUBE
      )
    },
    isDisableAhaSlideLink() {
      return this.planBenefit.brandingCustomisation && !this.planBenefit.brandingCustomisation.hideAhaSlidesLink
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.presentation.teamSize': function () {
      this.teamSize = this.$store.state.presentation.teamSize
    },
    defaultExpandedAccordion() {
      if (this.defaultExpandedAccordion === 'information') {
        this.$refs.presentInformation.click()
      }
    },
  },
  created() {
    this.teamPlay = this.presentation.teamPlay
    this.teamCount = this.presentation.teamCount
    this.teamSize = this.presentation.teamSize
  },
  mounted() {
    EventBus.$on('Settings: defaultExpandedAccordion', (name) => {
      if (!name || !name.length) return
      if (name === 'quiz') {
        this.$refs.generalQuizSettings.toggle()
      }
    })
    EventBus.$on('SettingsTabContent: update-presentation-quiz-team-configs', (payload) => {
      this._updatePresentationFromQuizTeamConfig(payload)
    })
  },
  destroyed() {
    EventBus.$off('Settings: defaultExpandedAccordion')
    EventBus.$off('SettingsTabContent: update-presentation-quiz-team-configs')
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'patchPresentationAction']),
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),

    setShowAhaSlidesLogo() {
      const payload = {
        notShowAhaSlidesLogo: this.$store.state.presentation
          .notShowAhaSlidesLogo,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
    },

    handleUpdateProfanityFilter() {
      const payload = {
        socketId: Socket.getSocketId(),
        filteringProfanity: this.presentation.filteringProfanity,
      }
      this.setSyncLoadingAction(1)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        `Header > Settings > Profanity Filter > ${this.presentation.filteringProfanity ? 'Filter on' : 'Filter off'}`,
      )
      this._updatePresentation(payload)
    },
    clickOutsideParticipantInfo() {
      this.ctaUpgradeParticipantInfo = false
    },
    clickOutsideAudienceAhaLabel() {
      this.ctaUpgradeAudienceAhaLabel = false
    },
    openUpgradeBackground() {
      if (!this.isPlanFree) {
        return
      }
      this.ctaUpgradeParticipantInfo = true
    },

    openUpgradeAudienceAhaSlideLabel() {
      if (!this.isPlanFree) {
        return
      }
      this.ctaUpgradeAudienceAhaLabel = true
    },

    handleEnablePublicSearchPresentation() {
      const payload = {
        isPublicSearch: this.presentation.isPublicSearch,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
    },

    _updatePresentation(payload) {
      this.$http
        .put(Config.api.updatePresentation + this.$route.params.id, payload)
        .then(
          () => {
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
    },

    // copied from _updatePresentation method in QuizTeamConfig.vue
    async _updatePresentationFromQuizTeamConfig(payload) {
      payload.uniqueAccessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      this.setSyncLoadingAction(1)
      try {
        await PresentationApi.updatePresentationByPresentationId(
          this.$store.state.presentation.id,
          payload,
        )
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Util.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team set up > Quiz team scoring rule or play update failed',
          JSON.stringify(error),
        )

        if (error.status) {
          throw error
        }
      }
    },

    setLang() {
      this.$store.dispatch('setLang', this.$store.state.presentation.language)
      this.setSyncLoadingAction(1)
      this.$http
        .put(
          Config.api.updatePresentation + this.$store.state.presentation.id,
          {
            language: this.$store.state.presentation.language,
            socketId: Socket.getSocketId(),
          },
        )
        .then(
          () => {
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Change Language > Language changed',
        this.$store.state.presentation.language,
      )
    },

    openModalUploadLogo() {
      this.showModalUploadLogo = true
    },
    hideModalUploadLogo() {
      this.modalUploadLogo = false
    },
    handleUploadImageForm(data) {
      this._hideModalUploadImage()
      this.imageUrlCrop = data
      this.showCropImage = true
    },
    setFontFamily() {
      this.setSyncLoadingAction(1)
      const payload = {
        fontFamily: this.$store.state.presentation.fontFamily,
        socketId: Socket.getSocketId(),
        accessCode: this.$store.state.presentation.uniqueAccessCode,
      }
      Util.setFontSlideV2(this.$store.state.presentation.fontFamily)
      this._updatePresentation(payload)
    },
    onChangeAudiencePacing() {
      const payload = {
        socketId: Socket.getSocketId(),
        audiencePacing: this.$store.state.presentation.audiencePacing,
      }
      this.setSyncLoadingAction(1)
      this._updatePresentation(payload)
      let name = 'presenter'
      if (this.$store.state.presentation.audiencePacing) {
        name = 'audience'
      }
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Who takes the lead', name)
    },
    _hideInstructionBar() {
      this.$store.state.presentation.hideIntroBarDocument =
        this.$store.state.presentation.hideInstructionBar
      const payload = {
        socketId: Socket.getSocketId(),
        hideInstructionBar: this.$store.state.presentation.hideInstructionBar,
        hideIntroBarDocument: this.$store.state.presentation.hideInstructionBar,
      }
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Presenter show/hide instruction bar',
        payload.hideInstructionBar,
      )
    },
    togglePrivateFeedback() {
      const payload = {
        isEnablePrivateFeedback: this.presentation.isEnablePrivateFeedback,
        socketId: Socket.getSocketId(),
      }

      this.$store.dispatch('updatePresentation', payload)
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Toggle private feedback',
        payload.isEnablePrivateFeedback ? 'Enable' : 'Disable',
        null, {
          presentationId: this.presentation.id,
        },
      )
    },

    async handleEnableSingleReaction(reactionName, reactionEnabled) {
      this.setSyncLoadingAction(1)
      const payload = {
        reactions: {
          ...this.presentation.reactions,
        },
      }
      payload.reactions[reactionName] = reactionEnabled
      this.patchPresentationAction(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Enable reaction',
        `Reaction ${reactionEnabled ? 'enabled' : 'disabled'}: ${reactionName}`,
      )
      try {
        await PresentationApi.patchPresentation(this.presentation.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },

    _linkClickAble() {
      const payload = {
        showHyperLink: this.$store.state.presentation.showHyperLink,
      }
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Presenter show/hide hyperlink',
        payload.showHyperLink,
      )
    },

    showShareMenu() {
      this.$store.state.presentation.showShare = !this.$store.state.presentation.showShare
      this.$store.dispatch('updatePresentation', this.$store.state.presentation)
      this.$store.state.presentation.showSetting = false
      this.$store.state.presentation.showExport = false
      this.$store.state.presentation.showBranding = false

      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Editor Header > Setting > Who takes the lead > Open Invite menu')

      if (!this.$store.state.presentation.qrcode
        || !this.$store.state.presentation.shareCode
        || !this.$store.state.presentation.uniqueAccessCode) {
        this.$http.get(Config.api.getPresentationBasic + this.$store.state.presentation.id)
          .then((res) => {
            this.$store.state.presentation.qrcode = res.body.qrcode
            this.$store.state.presentation.shareCode = res.body.shareCode
            this.$store.state.presentation.uniqueAccessCode = res.body.uniqueAccessCode
          })
      }
    },
    onClickPresenInfoTab() {
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Settings menu > Presentation info')
    },
    openUpgrade() {
      this.ctaUpgrade = true

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Feature upgrade',
        'Show sign-up link',
      )
    },
    clickOutsideUp() {
      this.ctaUpgrade = false
    },
    handleModalUpgradeApply() {
      this.ctaUpgrade = false
      this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },

    toggleAudienceReview() {
      const payload = {
        isEnableAudienceReviewSlides: this.presentation.isEnableAudienceReviewSlides,
        socketId: Socket.getSocketId(),
      }

      this.$store.dispatch('updatePresentation', payload)
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings >  Allow audience to review the slides',
        !!payload.isEnableAudienceReviewSlides,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },

    toggleAudienceAhaSlideLabel() {
      const payload = {
        isEnableAudienceAhaSlideLabel: this.presentation.isEnableAudienceAhaSlideLabel,
        socketId: Socket.getSocketId(),
      }

      this.$store.dispatch('updatePresentation', payload)
      this._updatePresentation(payload)
      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Other settings > Show AhaSlides link at the end of the last slide',
        !!payload.isEnableAudienceAhaSlideLabel,
        null,
        {
          presentationId: this.presentation.id,
        },
      )
    },
    handleCheckNeedUpgradePlanSlideReview() {
      if (this.planBenefit.privacyControl && !this.planBenefit.privacyControl.slidesReviewControl) {
        this.$refs.iconNeedUpgradePlanComponentSlidesReview.toggleTooltip()
      }
    },
    handleCheckNeedUpgradeHideSlideLink() {
      if (this.planBenefit.brandingCustomisation && !this.planBenefit.brandingCustomisation.hideAhaSlidesLink) {
        this.$refs.iconNeedUpgradePlanComponentHideSlideLink.toggleTooltip()
      }
    },

    handleHideMainHeader() {
      EventBus.$emit('EditorHeader: toggle-team-setup')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.settings-panel {
  @apply overflow-y-auto;
  height: calc(100% - $right-panel-header-height);
  margin-top: $right-panel-header-height;
}
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
}

.settings-reaction__checkbox {
  @apply flex flex-col-reverse mr-4;
  &::v-deep .checkbox-component-wrapper.mr-2 {
    margin-right: 0 !important;
  }
  .settings-reaction__image {
    @apply mb-2;
    width: 25px;
    height: 25px;
  }
}

.settings-accordion {
  & ::v-deep {
    .accordion-item--expanded {
      .settings-accordion__badge {
        display: none;
      }
    }
    .accordion-item-title-content {
      padding-left: 15px;
    }
  }
}

.cta-upgrade {
  @apply bg-secondary-100 absolute w-60 p-3 rounded z-50;
  top: -9rem;
  left: calc(50% - 120px);
  div,
  span {
    @apply text-base-0 text-sm;
  }
  div {
    @apply font-bold;
  }
  span {
    @apply font-normal mt-1;
  }
}
.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, var(--tw-bg-opacity)) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 1.6rem;

  &.upgrade-arrow_presentation {
    left: calc(50% - 47px);
  }

  &.upgrade-arrow_slide {
    left: 10%;
  }
}
.aha-link--primary-type.aha-link--primary {
  color: #6A1EBB !important; // overwrite Bootstrap CSS
  &:hover, &:active {
    color: #621BAF !important; // overwrite Bootstrap CSS
  }
}
.other-settings__content-wrapper {
  height: 32px;
  display: flex;
  align-items: center;
  label {
    margin-bottom: 0;
  }
}
.fa-question-circle {
  color: #b7b7b7;
}
.viewer-info-box {
  @apply p-4 text-xs;
}
</style>
